const GAP = 8
const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

function createWindow(el, value, component) {
  let resize = value
  let panel, header, originalStyle, maximizeButton, restoreRect, frame, move, up
  let maximized = false
  let destroyed = false
  const controls = []
  const bounds = () => ({ width: Math.max(1, window.innerWidth - GAP * 2), height: Math.max(1, window.innerHeight - GAP * 2) })
  const readRect = () => {
    const rect = panel.getBoundingClientRect()
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
  }
  const notifyResize = () => {
    // 连续拖动只在下一帧通知地图，避免重复调整画布。
    if (frame) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      frame = null
      if (panel && typeof resize === 'function') resize()
    })
  }
  const applyRect = rect => {
    const available = bounds()
    const width = clamp(rect.width, Math.min(360, available.width), available.width)
    const height = clamp(rect.height, Math.min(240, available.height), available.height)
    Object.assign(panel.style, {
      position: 'fixed', margin: '0', right: 'auto', bottom: 'auto',
      left: `${clamp(rect.left, GAP, window.innerWidth - GAP - width)}px`,
      top: `${clamp(rect.top, GAP, window.innerHeight - GAP - height)}px`,
      width: `${width}px`, height: `${height}px`
    })
  }
  const stopPointer = () => {
    if (up) up()
  }
  const startPointer = (event, edge) => {
    if (event.button !== 0 || maximized || (!edge && event.target.closest('button, input, a, [role="button"]'))) return
    event.preventDefault()
    stopPointer()
    const rect = readRect()
    const startX = event.clientX
    const startY = event.clientY
    const selection = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    move = current => {
      const dx = current.clientX - startX
      const dy = current.clientY - startY
      if (!edge) {
        applyRect({ ...rect, left: rect.left + dx, top: rect.top + dy })
        return
      }
      const available = bounds()
      const minWidth = Math.min(360, available.width)
      const minHeight = Math.min(240, available.height)
      let left = rect.left, top = rect.top, right = rect.left + rect.width, bottom = rect.top + rect.height
      if (edge.includes('w')) left = clamp(rect.left + dx, GAP, right - minWidth)
      if (edge.includes('e')) right = clamp(right + dx, left + minWidth, window.innerWidth - GAP)
      if (edge.includes('n')) top = clamp(rect.top + dy, GAP, bottom - minHeight)
      if (edge.includes('s')) bottom = clamp(bottom + dy, top + minHeight, window.innerHeight - GAP)
      applyRect({ left, top, width: right - left, height: bottom - top })
      notifyResize()
    }
    up = () => {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
      document.body.style.userSelect = selection
      move = null
      up = null
    }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }
  const drag = event => startPointer(event)
  const toggleMaximize = () => {
    if (!panel) return
    stopPointer()
    if (maximized) applyRect(restoreRect)
    else {
      restoreRect = readRect()
      applyRect({ left: GAP, top: GAP, ...bounds() })
    }
    maximized = !maximized
    panel.classList.toggle('dialog-window--maximized', maximized)
    maximizeButton.setAttribute('aria-label', maximized ? '还原窗口' : '最大化窗口')
    maximizeButton.title = maximized ? '还原窗口' : '最大化窗口'
    maximizeButton.firstChild.className = maximized ? 'el-icon-copy-document' : 'el-icon-full-screen'
    notifyResize()
  }
  const fitViewport = () => {
    if (!panel) return
    applyRect(maximized ? { left: GAP, top: GAP, ...bounds() } : readRect())
    notifyResize()
  }
  const close = () => {
    stopPointer()
    if (frame) cancelAnimationFrame(frame)
    frame = null
    window.removeEventListener('resize', fitViewport)
    if (!panel) return
    header.removeEventListener('mousedown', drag)
    maximizeButton.removeEventListener('click', toggleMaximize)
    controls.splice(0).forEach(control => control.remove())
    panel.classList.remove('dialog-window', 'dialog-window--maximized')
    // 恢复原始定位，让下次打开继续沿用组件的默认尺寸和进入动画。
    panel.style.cssText = originalStyle
    panel = null
    maximized = false
    restoreRect = null
  }
  const open = () => {
    if (destroyed || resize === false || panel || (component && !component.visible)) return
    panel = el.querySelector('.el-dialog, .el-drawer')
    header = panel && panel.querySelector('.el-dialog__header, .el-drawer__header')
    if (!header) { panel = null; return }
    originalStyle = panel.style.cssText
    const rect = readRect()
    panel.classList.add('dialog-window')
    applyRect(rect)
    header.addEventListener('mousedown', drag)
    maximizeButton = document.createElement('button')
    maximizeButton.type = 'button'
    maximizeButton.className = 'dialog-window__maximize'
    maximizeButton.setAttribute('aria-label', '最大化窗口')
    maximizeButton.title = '最大化窗口'
    const icon = document.createElement('i')
    icon.className = 'el-icon-full-screen'
    maximizeButton.appendChild(icon)
    maximizeButton.addEventListener('click', toggleMaximize)
    header.appendChild(maximizeButton)
    controls.push(maximizeButton)
    for (const edge of ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']) {
      const handle = document.createElement('div')
      handle.className = `dialog-window__resize dialog-window__resize--${edge}`
      handle.setAttribute('aria-hidden', 'true')
      handle.addEventListener('mousedown', event => startPointer(event, edge))
      panel.appendChild(handle)
      controls.push(handle)
    }
    window.addEventListener('resize', fitViewport)
    notifyResize()
  }
  if (component) {
    component.$on('opened', open)
    component.$on('close', stopPointer)
    component.$on('closed', close)
    component.$nextTick(open)
  }
  el.addEventListener('dialog-window-toggle', toggleMaximize)
  return {
    update(value) {
      resize = value
      if (value === false) close()
      else if (component && !component.visible) stopPointer()
    },
    destroy() {
      destroyed = true
      close()
      el.removeEventListener('dialog-window-toggle', toggleMaximize)
      if (component) {
        component.$off('opened', open)
        component.$off('close', stopPointer)
        component.$off('closed', close)
      }
    }
  }
}

export default {
  inserted(el, binding, vnode) { el.__dialogWindow = createWindow(el, binding.value, vnode.componentInstance) },
  componentUpdated(el, binding) { if (el.__dialogWindow) el.__dialogWindow.update(binding.value) },
  unbind(el) {
    if (el.__dialogWindow) el.__dialogWindow.destroy()
    delete el.__dialogWindow
  }
}
